import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Divider
} from '@mui/material';
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logout } from '../store/slices/authSlice';
import { changePassword, clearError } from '../store/slices/profileSlice';

const ChangePassword: React.FC = () => {
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [success, setSuccess] = useState<string | null>(null);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    
    const { loading, error } = useAppSelector((state) => state.profile);

    // Clear error when component unmounts or when error changes
    useEffect(() => {
        return () => {
            dispatch(clearError());
        };
    }, [dispatch]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear errors when user starts typing
        if (error) dispatch(clearError());
        if (success) setSuccess(null);
    };

    const handleTogglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validateForm = () => {
        if (!formData.currentPassword) {
            return 'Current password is required';
        }
        if (!formData.newPassword) {
            return 'New password is required';
        }
        if (formData.newPassword.length < 8) {
            return 'New password must be at least 8 characters long';
        }
        if (formData.newPassword === formData.currentPassword) {
            return 'New password must be different from current password';
        }
        if (formData.newPassword !== formData.confirmPassword) {
            return 'New password and confirm password do not match';
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const validationError = validateForm();
        if (validationError) {
            return;
        }

        setSuccess(null);

        try {
            await dispatch(changePassword({
                current_password: formData.currentPassword,
                new_password: formData.newPassword,
                new_password_confirmation: formData.confirmPassword
            })).unwrap();

            setSuccess('Password changed successfully! You will be logged out and redirected to login page.');
            
            // Clear form
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            
            // Logout and redirect to login after 3 seconds
            setTimeout(() => {
                dispatch(logout());
                navigate('/login');
            }, 3000);

        } catch (err: any) {
            // Error is handled by the slice
            console.error('Change password failed:', err);
        }
    };

    return (
        <Box maxWidth={600} mx="auto" mt={4} px={2}>
            <Card>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Lock sx={{ fontSize: 32, color: 'secondary.main', mr: 2 }} />
                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            Change Password
                        </Typography>
                    </Box>
                    
                    <Divider sx={{ mb: 3 }} />

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            {success}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Current Password"
                            name="currentPassword"
                            type={showPasswords.current ? 'text' : 'password'}
                            value={formData.currentPassword}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            InputProps={{
                                endAdornment: (
                                    <Button
                                        onClick={() => handleTogglePasswordVisibility('current')}
                                        sx={{ minWidth: 'auto', p: 1 }}
                                    >
                                        {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                                    </Button>
                                )
                            }}
                        />

                        <TextField
                            fullWidth
                            label="New Password"
                            name="newPassword"
                            type={showPasswords.new ? 'text' : 'password'}
                            value={formData.newPassword}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            helperText="Password must be at least 8 characters long"
                            InputProps={{
                                endAdornment: (
                                    <Button
                                        onClick={() => handleTogglePasswordVisibility('new')}
                                        sx={{ minWidth: 'auto', p: 1 }}
                                    >
                                        {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                    </Button>
                                )
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Confirm New Password"
                            name="confirmPassword"
                            type={showPasswords.confirm ? 'text' : 'password'}
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            InputProps={{
                                endAdornment: (
                                    <Button
                                        onClick={() => handleTogglePasswordVisibility('confirm')}
                                        sx={{ minWidth: 'auto', p: 1 }}
                                    >
                                        {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                                    </Button>
                                )
                            }}
                        />

                        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                sx={{ flex: 1 }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Change Password'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/profile')}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ChangePassword; 